import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { apiClient } from "@/services/core/apiClient";
import { useState, useMemo, useCallback } from "react";
import LoaderComponent from "@/components/common/LoaderComponent";

const formSchema = z.object({
  workflowName: z.string().min(1, "Workflow Status Name is required"),
});

type FormData = z.infer<typeof formSchema>;

interface WorkflowStatusFormProps {
  initialData?: {
    id?: number;
    workflowName?: string;
  };
  isEdit?: boolean;
}

export const WorkflowStatusForm = ({ initialData, isEdit = false }: WorkflowStatusFormProps) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();
  const [isLoading, setIsLoading] = useState(false);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workflowName: initialData?.workflowName || "",
    },
  });

  const watchedWorkflowName = form.watch("workflowName");

  // Create stable reference for initial data comparison
  const initialDataRef = useMemo(() => ({
    workflowName: initialData?.workflowName || "",
  }), []);

  // Check if form has changes (for edit mode)
  const hasFormChanges = useCallback(() => {
    if (!isEdit) return true; // Always allow save in add mode

    const currentWorkflowName = watchedWorkflowName?.trim() || '';
    const initialWorkflowName = initialDataRef.workflowName.trim();
    
    return currentWorkflowName !== initialWorkflowName;
  }, [watchedWorkflowName, isEdit, initialDataRef]);

  const formHasChanges = hasFormChanges();

  const onSubmit = async (data: FormData) => {
    if (!user?.id || !token) {
      handleError(new Error("User not authenticated"), "Authentication Error");
      return;
    }

    if (isEdit && !initialData?.id) {
      handleError(new Error("Workflow Status ID is missing"), "Error");
      return;
    }

    // Check if the name has changed
    if (isEdit && data.workflowName === initialData?.workflowName) {
      handleError(new Error("No changes detected. Please modify the workflow status name to update."), "No Changes");
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = isEdit 
        ? `/api/ngpep/${user.id}/workflowStatus/${initialData.id}`
        : `/api/ngpep/${user.id}/workflowStatus`;
      
      const apiCall = isEdit
        ? apiClient.put(endpoint, { workflowStatus: data.workflowName }, token)
        : apiClient.post(endpoint, { workflowStatus: data.workflowName }, token);

      await apiCall;

      handleSuccess(
        `Workflow Status "${data.workflowName}" has been ${isEdit ? "updated" : "added"} successfully.`,
        isEdit ? "Workflow Status Updated" : "Workflow Status Added"
      );
      
      navigate("/admin/workflow?tab=workflow-status");
    } catch (error) {
      handleError(error, isEdit ? "Update Failed" : "Creation Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    navigate("/admin/workflow?tab=workflow-status");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          {isEdit ? "Edit Workflow Status" : "Add New Workflow Status"}
        </h1>
        <p className="text-muted-foreground mt-2">
          {isEdit ? "Update workflow status information" : "Create a new workflow status"}
        </p>
      </div>

      <div className="max-w-4xl">
        <Form {...form}>
          <form id="workflow_status_form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="workflowName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workflow Status Name</FormLabel>
                  <FormControl>
                    <Input 
                      id="workflow_status_form_name_input"
                      placeholder="Enter workflow status name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-4 justify-end pt-6">
              <Button id="workflow_status_form_cancel_btn" type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
              <Button id="workflow_status_form_submit_btn" type="submit" disabled={isLoading || (isEdit && !formHasChanges)}>
                {isLoading ? (
                  <LoaderComponent size="sm" />
                ) : (
                  isEdit ? "Update" : "Save"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};